import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Box, Paper } from '@mui/material';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tag } from '@/components/ui/tag';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Calendar, Download, Printer, Share2, Activity, BarChart3 } from 'lucide-react';
import { format } from 'date-fns';
import { getMealPlanForDisplay, getMealPlanDraftById, getManualMealPlanDraft, updateMealPlanDayName } from '@/lib/apiService';
import { showErrorToast, showSuccessToast, showWarningToast } from '@/utils/toast-utils';
import { DayNameEditPopover } from '@/components/meal-planning/DayNameEditPopover';
import RecipeDetailsModal from '@/components/meal-planning/RecipeDetailsModal';
import NutritionSummaryCard from '@/components/shared/NutritionSummaryCard';
import type { FinalizedMealPlanData } from '@/types/meal-plan';
import { formatRecipeSource } from '@/lib/utils';
import { getClientById } from '@/lib/api/clients';
import { Client } from '@/types/client';
import FeedbackButton from '@/components/feedback/FeedbackButton';
import { FeedbackContext } from '@/components/feedback/FeedbackPanel';
import ClientDetailsBadge from '@/components/meal-planning/ClientDetailsBadge';
import ClientGoalsCard from '@/components/meal-planning/ai/ClientGoalsCard';
import { getNormalizedCreationMethod, getPlanningFeedbackContext } from '@/utils/feedback';
import { resolveClientGoals, PlanGoalsInput, OverrideGoalsInput } from '@/utils/client-goals';
import { buildGoalDisplayItems } from '@/utils/nutritionGoals';
import { RecipeAllergenCard } from '@/components/recipes/RecipeAllergenCard';

import { DEFAULT_RECIPE_IMAGE } from '@/constants/media';

const FinalizedMealPlanPage = () => {
  const { clientId, planId } = useParams<{ clientId: string; planId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { from?: string; mealPlanData?: FinalizedMealPlanData } | null;
  const fallbackBackPath = clientId ? `/clients/${clientId}/details-v2/meal-plan` : '/meal-planning';
  const isDraftRoutePath = (path?: string | null) => {
    if (!path) return false;
    return (
      path.startsWith('/manual-meal-plan/') ||
      path.startsWith('/ai-meal-plan/draft/') ||
      path.startsWith('/automated-meal-plan/draft/')
    );
  };
  const rawBackTarget = locationState?.from;
  const cameFromDraftRoute = isDraftRoutePath(rawBackTarget);
  const backTarget = cameFromDraftRoute ? fallbackBackPath : rawBackTarget || fallbackBackPath;
  
  const [mealPlanData, setMealPlanData] = useState<FinalizedMealPlanData | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedRecipeId, setSelectedRecipeId] = useState<string | null>(null);
  const [selectedRecipeName, setSelectedRecipeName] = useState<string>('');
  const [selectedRecipeContext, setSelectedRecipeContext] = useState<{ day: number; mealName: string; source: string } | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [clientDetails, setClientDetails] = useState<Client | null>(null);
  const feedbackContext = useMemo<FeedbackContext>(() => {
    const normalizedMethod = getNormalizedCreationMethod(mealPlanData);
    return getPlanningFeedbackContext(normalizedMethod, {
      manual: FeedbackContext.MANUAL_MEAL_PLANNING_OVERALL,
      ai: FeedbackContext.AI_MEAL_PLANNING_OVERALL,
      auto: FeedbackContext.AUTOMATED_MEAL_PLANNING_OVERALL,
      fallback: FeedbackContext.OVERALL_APPLICATION,
    });
  }, [mealPlanData]);

  useEffect(() => {
    if (!planId) return;
    if (locationState?.mealPlanData) {
      setMealPlanData(locationState.mealPlanData);
      return;
    }
    fetchMealPlan();
  }, [planId]);

  // Redirect draft plans to the appropriate draft page
  useEffect(() => {
    if (!mealPlanData || !planId || !clientId) return;
    const status = (mealPlanData.status ?? '').toString().toLowerCase();
    if (status !== 'draft') return;
    if (cameFromDraftRoute) {
      return;
    }

    const method = getNormalizedCreationMethod(mealPlanData);
    const draftPath =
      method === 'auto_generated'
        ? `/automated-meal-plan/draft/${planId}/client/${clientId}`
        : method === 'ai_generated'
          ? `/ai-meal-plan/draft/${planId}/client/${clientId}`
          : method === 'manual'
            ? `/manual-meal-plan/${planId}/client/${clientId}`
            : null;
    if (draftPath) {
      navigate(draftPath, { replace: true });
    }
  }, [mealPlanData, planId, clientId, navigate, cameFromDraftRoute]);

  useEffect(() => {
    if (clientId) {
      fetchClientDetails(clientId);
    }
  }, [clientId]);

  const fetchMealPlan = async () => {
    setLoading(true);
    try {
      const response = await getMealPlanForDisplay(planId!);
      if (response.success && response.data) {
        setMealPlanData(response.data);
        return;
      }
      throw new Error(response.message || 'Failed to load meal plan');
    } catch {
      if (clientId) {
        const resolveDraftPath = (data: unknown) => {
          const method = getNormalizedCreationMethod(data);
          if (method === 'auto_generated') {
            return `/automated-meal-plan/draft/${planId}/client/${clientId}`;
          }
          if (method === 'ai_generated') {
            return `/ai-meal-plan/draft/${planId}/client/${clientId}`;
          }
          if (method === 'manual') {
            return `/manual-meal-plan/${planId}/client/${clientId}`;
          }
          return null;
        };

        try {
          const draftResponse = await getMealPlanDraftById(planId!);
          if (draftResponse.success && draftResponse.data) {
            const draftPath = resolveDraftPath(draftResponse.data);
            if (draftPath) {
              navigate(draftPath, { replace: true });
              return;
            }
          }
        } catch {
          // Continue to manual-draft endpoint fallback
        }

        try {
          const manualDraftResponse = await getManualMealPlanDraft(planId!);
          if (manualDraftResponse?.success && manualDraftResponse?.data) {
            const draftPath = resolveDraftPath(manualDraftResponse.data) ?? `/manual-meal-plan/${planId}/client/${clientId}`;
            navigate(draftPath, { replace: true });
            return;
          }
        } catch {
          // fall through to user-facing not-found toast below
        }
      }
      showErrorToast(new Error('Meal plan not found'), 'Error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (recipeId: string, recipeName: string, day: number, mealName: string, source: string, itemId?: string) => {
    const normalizedItemId = String(itemId ?? '').trim();
    if (!normalizedItemId) {
      showWarningToast('Item id is unavailable for this recipe.');
      return;
    }
    setSelectedRecipeId(recipeId);
    setSelectedRecipeName(recipeName);
    setSelectedRecipeContext({ day, mealName, source });
    setSelectedItemId(normalizedItemId);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    // Refresh the meal plan to get updated nutrition values
    fetchMealPlan();
  };

  const fetchClientDetails = async (id: string) => {
    try {
      const response = await getClientById(id);
      setClientDetails(response?.client ?? null);
    } catch (error) {
      console.error('Error fetching client details:', error);
      setClientDetails(null);
    }
  };

  const days = useMemo(
    () =>
      (mealPlanData?.days ?? []).filter(
        (d) => d != null && (d.day != null || (d as { dayNumber?: number }).dayNumber != null)
      ),
    [mealPlanData?.days]
  );

  const dayTabValues = useMemo(
    () =>
      days.map((day, dayIndex) =>
        String(day.day ?? (day as { dayNumber?: number }).dayNumber ?? dayIndex + 1)
      ),
    [days]
  );

  const [activeDayTab, setActiveDayTab] = useState<string>('1');
  const [editingDayNameFor, setEditingDayNameFor] = useState<number | null>(null);
  const [savingDayName, setSavingDayName] = useState(false);

  const searchParams = (mealPlanData?.searchParams as {
    clientGoals?: unknown;
    overrideClientGoals?: unknown;
  } | undefined) ?? undefined;

  const resolvedGoals = resolveClientGoals({
    clientDetails,
    planGoals: (mealPlanData?.targetDaily ?? searchParams?.clientGoals ?? null) as PlanGoalsInput | null,
    overrideGoals: (searchParams?.overrideClientGoals ?? null) as OverrideGoalsInput | null,
    macroRangeFallback: 'whenClientGoalMissing',
  });
  const goalItems = buildGoalDisplayItems(resolvedGoals);

  useEffect(() => {
    if (dayTabValues.length === 0) return;
    setActiveDayTab((previous) => (dayTabValues.includes(previous) ? previous : dayTabValues[0]));
  }, [dayTabValues]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading meal plan...</p>
        </div>
      </div>
    );
  }

  if (!mealPlanData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="text-gray-400 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Meal Plan Found</h3>
          <p className="text-gray-600 mb-4">Unable to load the meal plan.</p>
          <Button onClick={() => navigate(backTarget)}>
            Back to Meal Plans
          </Button>
        </div>
      </div>
    );
  }

  const nutritionSummary = mealPlanData.nutritionSummary;
  const dailyAvg = nutritionSummary?.dailyAverages?.macros;
  const fallbackClientName =
    ((mealPlanData as unknown as { clientName?: string })?.clientName ??
      ((mealPlanData.searchParams as unknown as { clientName?: string })?.clientName)) ||
    ((mealPlanData.searchParams as unknown as { clientDetails?: { name?: string; fullName?: string } })?.clientDetails?.fullName ??
      (mealPlanData.searchParams as unknown as { clientDetails?: { name?: string } })?.clientDetails?.name) ||
    ((mealPlanData.searchParams as unknown as { clientInfo?: { fullName?: string } })?.clientInfo?.fullName) ||
    '';

  const clientDisplayName = (
    clientDetails?.fullName ||
    [clientDetails?.firstName, clientDetails?.lastName].filter(Boolean).join(' ') ||
    fallbackClientName
  ).trim();

  const planDuration = days.length;

  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
        }}
      >
        <div className="space-y-6 p-4 sm:p-5 lg:px-7 lg:pt-6 lg:pb-7">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(backTarget)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Finalized Meal Plan</h1>
            
          </div>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" type="button">
            <Download className="h-4 w-4 mr-2" />
            PDF
          </Button>
          <Button variant="outline" size="sm" type="button">
            <Download className="h-4 w-4 mr-2" />
            Instructions PDF
          </Button>
          <Button variant="outline" size="sm">
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button variant="outline" size="sm">
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </Button>
          <FeedbackButton
            clientId={clientId || mealPlanData?.clientId || undefined}
            mealPlanId={planId || undefined}
            label="Testing Feedback"
            defaultContext={feedbackContext}
            title="Testing Feedback – Finalized Meal Plan"
            description="Please provide feedback on this finalized meal plan, including recipe quality, nutritional analysis, and overall plan effectiveness. Please specify the date of feedback. Multiple feedback entries allowed."
            titlePlaceholder="e.g., Finalized meal plan quality issues"
            detailsPlaceholder="Describe the steps, date of feedback, and observations when reviewing this finalized meal plan."
            submitLabel="Save Feedback"
            successDescription="Thanks for helping us improve Finalized Meal Plans."
          />
        </div>
      </div>
      
      {/* Client Profile Card */}
      {clientId && clientDetails && (
        <ClientDetailsBadge
          clientId={clientId}
          clientDetails={clientDetails}
          clientDisplayName={clientDisplayName}
          variant="card"
          planDuration={planDuration}
          status={mealPlanData?.status || 'Finalized'}
                />
      )}
      <ClientGoalsCard goalItems={goalItems} />

      {/* Days Tabs */}
      <Tabs value={activeDayTab} onValueChange={setActiveDayTab} className="w-full">
        <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${days.length}, 1fr)` }}>
          {days.map((day, dayIndex) => {
            const dayNum = day.day ?? (day as { dayNumber?: number }).dayNumber ?? dayIndex + 1;
            const displayName = (day as { dayName?: string | null }).dayName?.trim() || `Day ${dayNum}`;
            return (
            <TabsTrigger key={dayNum} value={String(dayNum)} className="flex items-center justify-center gap-1.5">
              <Calendar className="h-4 w-4 shrink-0" />
              <span className="truncate">{displayName}</span>
              <span className="ml-1 shrink-0 text-xs opacity-75">
                {format(new Date(day.date ?? ''), 'MMM dd')}
              </span>
              <DayNameEditPopover
                open={editingDayNameFor === dayNum}
                onOpenChange={(open) => {
                  if (!open) setEditingDayNameFor(null);
                  else setEditingDayNameFor(dayNum);
                }}
                dayNumber={dayNum}
                value={(day as { dayName?: string | null }).dayName ?? ''}
                saving={savingDayName}
                hasExistingName={!!(day as { dayName?: string | null }).dayName}
                onSave={async (valueToSave) => {
                  if (!planId) return;
                  setSavingDayName(true);
                  try {
                    const res = await updateMealPlanDayName(planId, {
                      day: dayNum,
                      dayName: valueToSave || null,
                    });
                    if (res?.success) {
                      setMealPlanData((prev) =>
                        prev
                          ? {
                              ...prev,
                              days: prev.days.map((d, i) =>
                                i === dayIndex ? { ...d, dayName: valueToSave || null } : d
                              ),
                            }
                          : null
                      );
                      showSuccessToast(res?.message ?? 'Day name updated');
                      setEditingDayNameFor(null);
                    } else {
                      showErrorToast(res?.message ?? 'Failed to update day name');
                    }
                  } catch {
                    showErrorToast('Failed to update day name');
                  } finally {
                    setSavingDayName(false);
                  }
                }}
                onClear={async () => {
                  if (!planId || !(day as { dayName?: string | null }).dayName) return;
                  setSavingDayName(true);
                  try {
                    const res = await updateMealPlanDayName(planId, { day: dayNum, dayName: null });
                    if (res?.success) {
                      setMealPlanData((prev) =>
                        prev
                          ? { ...prev, days: prev.days.map((d, i) => (i === dayIndex ? { ...d, dayName: null } : d)) }
                          : null
                      );
                      showSuccessToast(res?.message ?? 'Day name cleared');
                      setEditingDayNameFor(null);
                    } else {
                      showErrorToast(res?.message ?? 'Failed to clear day name');
                    }
                  } catch {
                    showErrorToast('Failed to clear day name');
                  } finally {
                    setSavingDayName(false);
                  }
                }}
              />
            </TabsTrigger>
            );
          })}
        </TabsList>

        {days.map((day, dayIndex) => {
          const dayNum = day.day ?? (day as { dayNumber?: number }).dayNumber ?? dayIndex + 1;
          return (
          <TabsContent key={dayNum} value={String(dayNum)}>
            <div className="space-y-6">
              {/* Day Nutrition Summary - Using common component with goals comparison */}
              {day.dayTotals && (
                <div className="relative">
                  <NutritionSummaryCard
                    title={`Day ${dayNum} - Nutrition Summary`}
                    icon={Activity}
                    iconColor="text-green-600"
                    nutrition={{
                      calories: day.dayTotals.macros.calories || 0,
                      protein: day.dayTotals.macros.protein || 0,
                      carbs: day.dayTotals.macros.carbs || 0,
                      fat: day.dayTotals.macros.fat || 0,
                      fiber: day.dayTotals.macros.fiber || 0,
                    }}
                    borderColor="border-green-200"
                  />
                  <div className="absolute top-4 right-4">
                    <Button
                      onClick={() => {
                        // Open nutritional analysis in new tab
                        const returnPath = `${window.location.pathname}${window.location.search}`;
                        const url = `/meal-plans/${planId}/clients/${clientId}/nutrition/${dayNum}?from=${encodeURIComponent(returnPath)}`;
                        const absoluteUrl = `${window.location.origin}${url}`;
                        window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      size="sm"
                    >
                      <BarChart3 className="h-4 w-4 mr-2" />
                      Open Analysis
                    </Button>
                  </div>
                </div>
              )}

           
              {/* Meals List - prefer normalized day.meals (has correct nutrition) over detailed API data */}
              <div className="space-y-3">
                  {(() => {
                    const mealsToDisplay = day.meals || {};
                    
                    return Object.entries(mealsToDisplay).map(([mealType, mealData]: [string, any]) => {
                      const recipes = mealData.recipes || (mealData.recipeName ? [mealData] : []);
                      
                      if (recipes.length === 0) return null;
                      
                    return (
                      <Card key={mealType} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                            <div className="space-y-4">
                              {/* Meal Type Header */}
                              <div className="flex items-center gap-2">
                                <Tag variant="outlined" size="sm" className="capitalize">
                                  {mealType}
                                </Tag>
                              </div>
                              
                              {/* Render all recipes for this meal */}
                              {recipes.map((recipe: any, recipeIndex: number) => {
                                const recipeName = recipe.title || recipe.recipeName || '';
                                const recipeImage =
                                  recipe.image ||
                                  recipe.imageUrl ||
                                  recipe.recipeImage ||
                                  recipe.recipeImageUrl ||
                                  DEFAULT_RECIPE_IMAGE;
                                const recipeSource = recipe.source || recipe.recipeSource;
                                const recipeId = recipe.recipeId || recipe.id || '';
                                const itemIdForDetails = String(
                                  recipe.itemId ?? recipe.id ?? recipe._id ?? '',
                                ).trim();
                                const customNotes = recipe.customNotes || (recipe as any).customNotes;
                                
                                // Extract nutrition - handle macros object, flat values, or { quantity } nested format
                                const nut = recipe.nutrition;
                                const nutMacros = nut?.macros;
                                const getVal = (v: unknown): number => 
                                  typeof v === 'number' ? v : (v && typeof v === 'object' && 'quantity' in v ? Number((v as { quantity?: number }).quantity) || 0 : 0);
                                const nutrition = nutMacros ? {
                                  calories: nutMacros.calories ?? getVal(nut?.calories) ?? recipe.calories ?? 0,
                                  protein: nutMacros.protein ?? getVal(nut?.protein) ?? recipe.protein ?? 0,
                                  carbs: nutMacros.carbs ?? getVal(nut?.carbohydrate ?? nut?.carbs) ?? recipe.carbs ?? 0,
                                  fat: nutMacros.fat ?? getVal(nut?.total_fat ?? nut?.fat) ?? recipe.fat ?? 0,
                                  fiber: nutMacros.fiber ?? getVal(nut?.dietary_fibre ?? nut?.fiber) ?? recipe.fiber ?? 0,
                                } : (nut && (typeof nut.calories === 'number' || nut.calories?.quantity != null) ? {
                                  calories: typeof nut.calories === 'number' ? nut.calories : (nut.calories?.quantity ?? recipe.calories ?? 0),
                                  protein: typeof nut.protein === 'number' ? nut.protein : (nut.protein?.quantity ?? recipe.protein ?? 0),
                                  carbs: typeof (nut.carbohydrate ?? nut.carbs) === 'number' ? (nut.carbohydrate ?? nut.carbs) : ((nut.carbohydrate ?? nut.carbs)?.quantity ?? recipe.carbs ?? 0),
                                  fat: typeof (nut.total_fat ?? nut.fat) === 'number' ? (nut.total_fat ?? nut.fat) : ((nut.total_fat ?? nut.fat)?.quantity ?? recipe.fat ?? 0),
                                  fiber: typeof (nut.dietary_fibre ?? nut.fiber) === 'number' ? (nut.dietary_fibre ?? nut.fiber) : ((nut.dietary_fibre ?? nut.fiber)?.quantity ?? recipe.fiber ?? 0),
                                } : mealData.nutrition?.macros ?? null);
                                
                                return (
                                  <div key={recipeIndex} className={recipeIndex > 0 ? "pt-4 border-t border-gray-200" : ""}>
                          <div className="flex items-center gap-4">
                            {/* Meal Image - Small */}
                            <img
                                        src={recipeImage}
                                        alt={recipeName}
                              className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.onerror = null;
                                target.src = DEFAULT_RECIPE_IMAGE;
                              }}
                            />
                            
                            {/* Meal Info */}
                            <div className="flex-1 min-w-0">
                                        {recipeSource && (
                                          <Tag variant="neutral" size="sm" className="mb-1">
                                            {formatRecipeSource(recipeSource)}
                                          </Tag>
                                )}
                                        <h4 className="font-semibold text-gray-900 mb-1">{recipeName}</h4>
                              
                              {/* Nutrition - Inline */}
                                        {nutrition && (
                                <div className="flex gap-3 text-xs text-gray-600 flex-wrap">
                                  <span className="font-medium">
                                              <span className="text-orange-600 font-bold">{Math.round(nutrition.calories || 0)}</span> cal
                                  </span>
                                  <span>•</span>
                                  <span className="font-medium">
                                              <span className="text-blue-600 font-bold">{Math.round(nutrition.protein || 0)}g</span> protein
                                  </span>
                                  <span>•</span>
                                  <span className="font-medium">
                                              <span className="text-green-600 font-bold">{Math.round(nutrition.carbs || 0)}g</span> carbs
                                  </span>
                                  <span>•</span>
                                  <span className="font-medium">
                                              <span className="text-amber-600 font-bold">{Math.round(nutrition.fat || 0)}g</span> fat
                                  </span>
                                        </div>
                                      )}
                                      <RecipeAllergenCard
                                        allergens={recipe.allergens}
                                        compact
                                        maxVisible={3}
                                        className="mt-2"
                                      />
                            </div>

                            {/* Action Button */}
                            <Button 
                              size="sm" 
                              variant="outline"
                                        onClick={() => handleViewDetails(
                                          recipeId,
                                          recipeName,
                                          dayNum,
                                          mealType,
                                          recipeSource,
                                          itemIdForDetails || undefined,
                                        )}
                              className="flex-shrink-0"
                            >
                              View Recipe
                            </Button>
                          </div>

                          {/* Custom Notes - Below in compact */}
                                    {customNotes && (
                            <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded-lg text-sm">
                                        <span className="font-medium">Note:</span> {customNotes}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                        </CardContent>
                      </Card>
                    );
                    });
                  })()}
                </div>
            </div>
          </TabsContent>
          );
        })}
      </Tabs>

      {/* Recipe Details Modal */}
      {selectedRecipeId && selectedItemId && (
        <RecipeDetailsModal
          recipeId={selectedRecipeId}
          recipeName={selectedRecipeName}
          open={modalOpen}
          onOpenChange={setModalOpen}
          draftId={undefined}
          mealPlanId={planId || undefined}
          itemId={selectedItemId ?? undefined}
          day={selectedRecipeContext?.day}
          mealName={selectedRecipeContext?.mealName}
          source={selectedRecipeContext?.source as 'spoonacular' | 'edamam'}
          onClose={handleModalClose}
        />
      )}
    </div>
      </Paper>
    </Box>
  );
};

export default FinalizedMealPlanPage;
